import { cloneDeep, toInteger } from 'lodash';
import {
    createCanvas,
    createForeignObjectImageUrl,
    fetchVectorText,
    loadImageAsync,
    loadSvgAsync,
    serializerDomToString,
} from '@/utils/file-kit';
import { getLayerFreshDOM } from '@/utils/layer-kit';
import { transferXYToDistance } from '@/utils/math-kit';
import { VectorType, type ILayerProps, type IVectorLayerProps } from '@/version-types';
import {
    MAX_CANVAS_AREA,
    MAX_CANVAS_DIMENSION,
    SCALE_HEIGHT_RATIO,
    SCALE_RATIO,
    SCALE_WIDTH_RATIO,
} from '../constants';
import { renderDom } from '../views/render-dom';
import { checkIsUsingEffect } from './effect';
import { getTextContentDOMSelector } from './text';

type Options = {
    isPPT: boolean;
};

export class LayerCanvasCreator {
    private readonly layer: ILayerProps;
    private readonly layerData: IVectorLayerProps;

    private readonly enableOpacity: boolean;
    private readonly isUsingEffect: boolean;

    private drawLayer: ILayerProps | null = null; // 用于绘制带特效的图层数据

    private effectLayoutInfluence = {
        scaleWidth: 0,
        scaleHeight: 0,
        offsetY: 0,
        reflectionHeight: 0,
    };

    constructor(
        private identity: string,
        layer: ILayerProps,
        enableOpacity = true,
    ) {
        const layerData = layer.data as IVectorLayerProps;

        this.layer = layer;
        this.layerData = layerData;

        this.enableOpacity = enableOpacity;
        this.isUsingEffect = checkIsUsingEffect(layerData, {
            includeBlur: true,
        });
    }

    public getEffectLayoutInfluence() {
        return this.effectLayoutInfluence;
    }

    private async loadSVGContent() {
        const { url } = this.layerData;
        if (url) return fetchVectorText(url);
    }

    /**
     * 根据特效（阴影、倒影、模糊等）计算出图所需的 canvas 尺寸，并调整 drawLayer 的定位。
     *
     * 特效会溢出图层原始边界（如阴影向外扩散、模糊边缘发散、倒影延伸到图层下方或上方），
     * 如果 canvas 大小等于图层大小，溢出部分会被裁切。因此需要将 canvas 按一定比率放大，
     * 为特效渲染预留安全边距。
     *
     * 放大后的 canvas 以图层内容为中心：
     * - widthDelta / heightDelta 是单侧的扩展量，drawLayer 的 left/top 被设置为该值，
     *   使图层内容绘制在放大后 canvas 的中央区域
     * - 倒影在图层上方时需要额外的 offsetY 补偿，确保图层内容不会被倒影区域挤压
     *
     * 当图层尺寸较大时，固定的放大比率可能导致 canvas 总像素数超出浏览器限制
     * （如 7900×4211 × 3 = 23700×12633 ≈ 299M 像素，超过浏览器 ~268M 的上限），
     * 此时会动态降低比率以确保 canvas 可用。
     *
     * 计算结果通过 effectLayoutInfluence 暴露给 ResultCanvasCreator，
     * 用于在最终合成阶段反向补偿放大偏移，保持图层在舞台上的正确位置。
     *
     * @param options - 配置项
     *  - isPPT: 是否是PPT模式
     * @returns - 计算出的 canvas 尺寸
     */
    private resizeLayerByEffect(options: Options) {
        const isPPTMode = options.isPPT;

        const { layer } = this;
        this.drawLayer = cloneDeep(this.layer);

        const effect = this.layerData.effect;
        const shadow = effect.shadow[0];
        const reflection = effect.reflection[0];

        let shadowOffsetX = 0;
        if (shadow) {
            shadowOffsetX = transferXYToDistance(shadow.x, shadow.y);
        }

        let reflectionOffsetY = 0;
        let reflectionHeight = 0;
        if (reflection) {
            reflectionOffsetY = reflection.y;
            reflectionHeight = reflection.size ? (reflection.size / 100) * layer.height : 0;
        }

        const totalWidth = layer.width;
        let totalHeight = layer.height;

        /**
         * 倒影的三种情况：
         * 1. 倒影完全在图层下方
         * 2. 倒影和图层有重叠
         * 3. 倒影完全在图层上方
         *
         * 对高度计算的影响：
         * 情况 1、2：即 reflectionOffsetY 大于等于 -reflectionHeight， totalHeight = 图层高度 + 偏移距离 + 倒影大小
         * 情况 3：即 reflectionOffsetY 小于 -layer.height， totalHeight = 偏移距离（此时偏移距离为图层底部到倒影顶部）
         */

        let offsetY = 0;

        if (reflectionOffsetY >= -reflectionHeight) {
            totalHeight = layer.height + reflectionHeight + reflectionOffsetY;
        } else if (reflectionOffsetY < -layer.height) {
            totalHeight = Math.abs(reflectionOffsetY);
            offsetY = totalHeight - layer.height;
        }

        let widthRatio = !isPPTMode ? SCALE_RATIO : SCALE_WIDTH_RATIO;
        let heightRatio = !isPPTMode ? SCALE_RATIO : SCALE_HEIGHT_RATIO;

        const contentWidth = totalWidth + shadowOffsetX;
        const contentHeight = totalHeight + shadowOffsetX;

        const rawWidth = Math.ceil(contentWidth * widthRatio);
        const rawHeight = Math.ceil(contentHeight * heightRatio);
        const rawArea = rawWidth * rawHeight;

        // 固定 SCALE_RATIO 在大图层上会超过浏览器 Canvas 限制，等比降至安全倍数
        if (rawArea > MAX_CANVAS_AREA || rawWidth > MAX_CANVAS_DIMENSION || rawHeight > MAX_CANVAS_DIMENSION) {
            const areaScale = Math.sqrt(MAX_CANVAS_AREA / (contentWidth * contentHeight));
            const dimScale = Math.min(MAX_CANVAS_DIMENSION / contentWidth, MAX_CANVAS_DIMENSION / contentHeight);
            const safeScale = Math.max(1, Math.min(areaScale, dimScale));
            widthRatio = safeScale;
            heightRatio = safeScale;
        }

        const finalWidth = Math.ceil(contentWidth * widthRatio);
        const finalHeight = Math.ceil(contentHeight * heightRatio);

        const widthDelta = Math.floor((finalWidth - totalWidth) / 2);
        const heightDelta = Math.floor((finalHeight - totalHeight) / 2);

        const nextLeft = widthDelta;
        const nextTop = heightDelta + offsetY;

        this.drawLayer.left = nextLeft;
        this.drawLayer.top = nextTop;
        this.drawLayer.rotate = 0;

        this.effectLayoutInfluence = {
            scaleWidth: widthDelta,
            scaleHeight: heightDelta,
            offsetY,
            reflectionHeight: totalHeight - layer.height,
        };

        return {
            width: finalWidth,
            height: finalHeight,
        };
    }

    /**
     * 创建带特效的图层 canvas。
     *
     * 流程：
     * 1. 通过 resizeLayerByEffect 计算放大后的 canvas 尺寸，获得带安全边距的画布
     * 2. 使用 renderDom 将 drawLayer（已调整 left/top/rotate）渲染为离屏 DOM
     * 3. 克隆渲染出的 DOM 元素，通过 CSS transform 将图层内容定位到放大 canvas 的中心：
     *    - translateX：水平居中，即 (canvasWidth - layerWidth) / 2
     *    - translateY：垂直方向需考虑倒影位置的影响：
     *      · 无倒影或倒影在下方：从垂直居中位置向上偏移 reflectionHeight/2，为下方倒影腾出空间
     *      · 倒影在图层上方：从垂直居中位置向下偏移 offsetY/2，远离上方的倒影区域
     * 4. 移除不需要的 DOM 节点（文本内容、reactive-area）
     * 5. 将 DOM 序列化为 SVG foreignObject，加载为图片后绘制到 canvas
     *
     * @param options - 配置项
     *  - isPPT: 是否是PPT模式
     * @returns - 创建的 canvas
     */
    private async createCanvasWithEffect(options: Options) {
        const { width, height } = this.resizeLayerByEffect(options);
        const drawLayer = this.drawLayer as ILayerProps;

        const dispose = await renderDom(this.identity, drawLayer);

        const selector = `[data-layer-id^="${this.layer.id}"]`; // 这里需要用前缀匹配，因为系统可能对图层id加上某些后缀

        const element = getLayerFreshDOM(selector);

        if (!element) {
            dispose?.();
            return null;
        }

        // 图片置于中心
        const cloned = element.cloneNode(true) as HTMLElement;
        const translateX = (width - drawLayer.width) / 2;
        const translateY = (() => {
            const baseDelta = (height - drawLayer.height) / 2;

            // case: 倒影完全在图层上方
            if (this.effectLayoutInfluence.offsetY) {
                return baseDelta + this.effectLayoutInfluence.offsetY / 2;
            }

            return baseDelta - this.effectLayoutInfluence.reflectionHeight / 2;
        })();

        cloned.style.transformOrigin = '50% 50%';
        cloned.style.transform = `translate(${translateX}px, ${translateY}px) rotate(0deg)`;

        // 需要将文本内容去除，文本另外使用文本图层的出图能力
        const textContentSelector = getTextContentDOMSelector(this.layer.id);
        const textContentDOM = cloned.querySelector(textContentSelector);
        if (textContentDOM) textContentDOM.remove();

        // 去除 reactive-area (图层宽度缩小到最小时，图层内会多出 reactive-area)
        const reactiveAreaDOM = cloned.querySelector('.reactive-area');
        if (reactiveAreaDOM) reactiveAreaDOM.remove();

        const svgString = createForeignObjectImageUrl(width, height, serializerDomToString(cloned));
        const imageSource = await loadSvgAsync(svgString);

        dispose?.();

        const { canvas, ctx } = createCanvas(width, height);
        ctx.drawImage(imageSource, 0, 0);

        return canvas;
    }

    private async createNormalCanvas() {
        const selector = `[data-draw-id="svg-${this.layer.id}"]`;

        const dispose = await renderDom(this.identity, this.layer);
        let imageSource = getLayerFreshDOM(selector) as HTMLImageElement | null;

        if (!imageSource) {
            dispose?.();
            return null;
        }

        const { width, height } = this.layer;
        const { vectorType } = this.layerData;

        if (vectorType === VectorType.Ellipse) {
            const svgString = createForeignObjectImageUrl(width, height, serializerDomToString(imageSource));
            imageSource = await loadSvgAsync(svgString);
        } else {
            imageSource = await loadImageAsync(imageSource.src);
        }

        const { imageTransform } = this.layerData;
        const {
            flipH,
            flipV,
            scale,
            rotate: imgRotate,
            widthRatio,
            heightRatio,
            offsetX: offsetXRatio,
            offsetY: offsetYRatio,
        } = imageTransform;

        const resultWidth = toInteger(width);
        const resultHeight = toInteger(height);
        const offsetX = toInteger(offsetXRatio * widthRatio * width * scale);
        const offsetY = toInteger(offsetYRatio * heightRatio * height * scale);
        const originImgWidth = toInteger(width * widthRatio);
        const originImgHeight = toInteger(height * heightRatio);

        let imgOffsetX = resultWidth / 2;
        let imgOffsetY = resultHeight / 2;

        if (flipH) {
            imgOffsetX = imgOffsetX - offsetX;
        } else {
            imgOffsetX = imgOffsetX + offsetX;
        }

        if (flipV) {
            imgOffsetY = imgOffsetY - offsetY;
        } else {
            imgOffsetY = imgOffsetY + offsetY;
        }

        // --- 创建图层 canvas ---
        const { canvas: layerCanvas, ctx: layerCtx } = createCanvas(resultWidth, resultHeight);
        layerCtx.translate(imgOffsetX, imgOffsetY);

        const rotate = flipH || flipV ? -imgRotate : imgRotate;
        layerCtx.rotate((rotate * Math.PI) / 180);
        layerCtx.scale(scale * (flipH ? -1 : 1), scale * (flipV ? -1 : 1));
        layerCtx.drawImage(imageSource, -originImgWidth / 2, -originImgHeight / 2, originImgWidth, originImgHeight);

        // 根据是否应用透明度进行绘制
        if (!this.enableOpacity) {
            dispose?.();
            return layerCanvas;
        }

        const { opacity } = this.layer;
        const { canvas: finalCanvas, ctx: finalCtx } = createCanvas(resultWidth, resultHeight);
        finalCtx.globalAlpha = opacity;
        finalCtx.drawImage(layerCanvas, 0, 0, resultWidth, resultHeight);
        dispose?.();
        return finalCanvas;
    }

    public async create(options: Options) {
        try {
            await this.loadSVGContent();
        } catch (error) {
            console.log('load svg error', error);
            return createCanvas(1, 1).canvas;
        }

        if (this.isUsingEffect) {
            return this.createCanvasWithEffect(options);
        }

        return this.createNormalCanvas();
    }
}
